import React from 'react'

// +vailable space around DOMRect box in viewPort
// (DOMRect, viewPort) -> { direction: pixels }
const spaceAroundElement = (
  { left, top, right, bottom },
  { width, height },
) => ({
  left,
  top,
  right: width - right,
  bottom: height - bottom,
})

// Which cardinal direction from anchor node to place tethered child node
const tetherDirection = (
  { top, left, right, bottom }, // anchor position
  { height, width }, // child size
  order, // preferred position
) => {
  // is there enough room to place child
  const room: Record<string, boolean> = {
    t: top > height,
    l: left > width,
    b: bottom > height,
    r: right > width,
    c: true,
  }
  const names: Record<string, string> = {
    t: 'top',
    l: 'left',
    b: 'bottom',
    r: 'right',
    c: 'center',
  }
  const initials = (order.trim() || 'b t r l c')
    .toLowerCase()
    .split(/[-,\s]+/g)
    .map((token: string) => token.charAt(0))
  // first direction with enough free space
  const initial = initials.find((c: string) => room[c]) ?? initials[0]
  return names[initial]
}

const windowSize = () => ({
  width: document.documentElement.clientWidth,
  height: document.documentElement.clientHeight,
})

type Dimensions = {
  top: number
  left: number
  bottom: number
  right: number
  width: number
  height: number
  cx: number
  cy: number
  // only set for the child box, by placeTetheredChild below
  padding?: number
}

const getDimensions = (el: Element): Dimensions => {
  const { top, left, bottom, right, width, height } = el.getBoundingClientRect()
  const [cx, cy] = [left + width / 2, top + height / 2]
  return { top, left, bottom, right, width, height, cx, cy }
}

// place tethered child using css
// (Element, Element, [String]) -> String
export const placeTetheredChild = (
  anchorNode: Element | null,
  childNode: Element | null,
  order: string = '',
  shift = 0,
) => {
  if (!(anchorNode instanceof Element))
    throw new Error('anchor node must be a DOM Element')
  if (!(childNode instanceof Element)) {
    throw new Error('child node must be a DOM Element')
  }
  const viewPort = windowSize()
  const anchor = getDimensions(anchorNode)
  const child = getDimensions(childNode)
  child.padding = getPixelSize('padding-top')(childNode) + 1

  // get cardinal direction to shift tethered child element relative to anchor
  const space = spaceAroundElement(anchor, viewPort)
  const direction = tetherDirection(space, child, order)

  // calculate x and y offset in pixels
  let x =
    direction === 'left'
      ? anchor.left - child.width
      : direction === 'right'
        ? anchor.right
        : anchor.cx - child.width / 2
  x = Math.round(Math.max(0, Math.min(viewPort.width - child.width, x)))

  let y =
    direction === 'top'
      ? anchor.top - child.height
      : direction === 'bottom'
        ? anchor.bottom
        : anchor.cy - child.height / 2
  y = Math.round(Math.max(0, Math.min(viewPort.height - child.height, y)))

  if (direction === 'right') x += shift * child.width
  else if (direction === 'left') x -= shift * child.width
  else if (direction === 'top') y -= shift * child.height
  else if (direction === 'bottom') y += shift * child.height

  const [tx, ty] = [x + window.scrollX, y + window.scrollY].map(Math.round)

  return {
    arrowTransform:
      getArrowTransform({ x, y, direction, child, anchor }) +
      (shift ? ' translateY(100%)' : ''),
    childTransform: `translate(${tx}px, ${ty}px)`,
  }
}

// get pixel size of css property
// String -> Element -> Number
const getPixelSize = (property: string) => (element: Element) => {
  let size: string
  try {
    size = window.getComputedStyle(element).getPropertyValue(property)
  } catch {
    size = '0px'
  }
  return parseInt(size)
}

const getArrowTransform = ({
  x,
  y,
  direction,
  anchor,
  child,
  arrowSize = 14,
}) => {
  const placement = {
    top: { translateY: Math.max(0, child.height - arrowSize), rotate: 2 },
    left: { translateX: Math.max(0, child.width - arrowSize), rotate: 1 },
    right: { translateX: 0, rotate: 3 },
    bottom: { translateY: 0, rotate: 0 },
  }[direction] ?? {
    translateX: (child.width - arrowSize) / 2,
    translateY: (child.height - arrowSize) / 2,
    scale: 0.1,
  }
  const towardsAnchor = {
    translateX: Math.min(
      child.width - child.padding - arrowSize,
      Math.max(child.padding, anchor.cx - x - arrowSize * 0.5),
    ),
    translateY: Math.min(
      child.height - child.padding - arrowSize,
      Math.max(child.padding, anchor.cy - y - arrowSize * 0.5),
    ),
  }
  const {
    translateX = 0,
    translateY = 0,
    scale = 1,
    rotate = 0,
  } = { ...towardsAnchor, ...placement }
  return [
    `translateX(${Math.round(translateX)}px)`,
    `translateY(${Math.round(translateY)}px)`,
    `rotate(${rotate / 4}turn)`,
    `scale(${scale})`,
  ].join(' ')
}

// get or create the parent dom node for tether portals
const getTetherRoot = (id = 'tether-root') => {
  let node = document.getElementById(id)
  if (!node) {
    node = document.createElement('div')
    node.setAttribute('id', id)
    document.body.appendChild(node)
  }
  return node
}

// DOM Element visible in viewport
// Element -> Boolean
export const isElementVisible = element => {
  if (!element) return false
  const { width, height } = windowSize()
  const { top, bottom, left, right } = element.getBoundingClientRect()
  return bottom > 0 && right > 0 && left < width && top < height
}

// () -> node
export const usePortalContainer = () => {
  const portalRef = React.useRef<HTMLDivElement | null>(null)
  if (!portalRef.current) {
    const el = document.createElement('div')
    el.className = 'portalContainer'
    portalRef.current = el
  }
  React.useEffect(() => {
    // default parent is "body"
    const parent = getTetherRoot()
    // the ref is populated above, before this effect can run
    parent.appendChild(portalRef.current!)
    return () => portalRef.current!.remove()
  }, [])
  return portalRef.current
}
